import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Badge,
  Spinner,
  Alert,
} from "react-bootstrap";
import {
  DatatableWrapper,
  TableHeader,
  TableBody,
  Pagination,
  PaginationOptions,
  Filter,
} from "react-bs-datatable";
import {
  FaSearch,
  FaDownload,
  FaFilePdf,
  FaExclamationTriangle,
} from "react-icons/fa";
import reportService from "../../services/reportService";
import categoryService from "../../services/categoryService";
import subCategoryService from "../../services/subCategoryService";
import { productService } from "../../services/productService";
import InfoPill from "../Common/InfoPill";
import "./Report.css";
import { Link } from "react-router-dom";
const LowStockProductsReport = () => {
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [threshold, setThreshold] = useState(10);

  // Filters
  const [filters, setFilters] = useState({
    category_id: "",
    sub_category_id: "",
    product_id: "",
    threshold: 10,
  });

  // Ref to track latest filters for immediate access
  const filtersRef = useRef(filters);

  // Initialize ref with current filters whenever filters change
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  // Load categories and products for filters
  useEffect(() => {
    loadFilterOptions();
  }, []);

  // Load subcategories when category changes
  useEffect(() => {
    if (filters.category_id) {
      loadSubCategoriesByCategory(filters.category_id);
    } else {
      setSubCategories([]);
    }
  }, [filters.category_id]);

  // Load initial report data on mount
  useEffect(() => {
    loadReportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadFilterOptions = async () => {
    try {
      const [catsRes, prodsRes] = await Promise.all([
        categoryService.getAllCategories({ status: "Active" }),
        productService.getProducts({ limit: 1000 }),
      ]);

      setCategories(catsRes?.categories || catsRes || []);
      setProducts(prodsRes?.products || prodsRes || []);
    } catch (error) {
      console.error("Error loading filter options:", error);
    }
  };

  const loadSubCategoriesByCategory = async (categoryId) => {
    try {
      const response = await subCategoryService.getSubCategoriesByCategoryId(
        categoryId
      );
      const subCats =
        response?.subCategories ||
        response?.data?.subCategories ||
        response?.data ||
        response ||
        [];
      setSubCategories(Array.isArray(subCats) ? subCats : []);
    } catch (error) {
      console.error("Error loading subcategories:", error);
      setSubCategories([]);
    }
  };

  const loadReportData = async (filterParams = null) => {
    try {
      setLoading(true);
      // Use provided filters or fall back to state
      const filtersToUse = filterParams !== null ? filterParams : filters;

      // Clean filters - remove empty strings and only send valid filter values
      const cleanedFilters = {};
      if (
        filtersToUse.category_id !== undefined &&
        filtersToUse.category_id !== null &&
        filtersToUse.category_id !== ""
      ) {
        cleanedFilters.category_id = String(filtersToUse.category_id).trim();
      }
      if (
        filtersToUse.sub_category_id !== undefined &&
        filtersToUse.sub_category_id !== null &&
        filtersToUse.sub_category_id !== ""
      ) {
        cleanedFilters.sub_category_id = String(
          filtersToUse.sub_category_id
        ).trim();
      }
      if (
        filtersToUse.product_id !== undefined &&
        filtersToUse.product_id !== null &&
        filtersToUse.product_id !== ""
      ) {
        cleanedFilters.product_id = String(filtersToUse.product_id).trim();
      }
      // Always include threshold
      cleanedFilters.threshold = filtersToUse.threshold || threshold || 10;

      const response = await reportService.getLowStockProductsReport(
        cleanedFilters
      );

      // Handle different response structures
      let data = [];
      if (response) {
        if (response.products && Array.isArray(response.products)) {
          data = response.products;
        } else if (response.data && Array.isArray(response.data)) {
          data = response.data;
        } else if (Array.isArray(response)) {
          data = response;
        }
      }

      // Add serial number to each row
      const dataWithSrNo = Array.isArray(data)
        ? data.map((item, index) => ({
            ...item,
            sr_no: index + 1,
          }))
        : [];
      setReportData(dataWithSrNo);
    } catch (error) {
      console.error("Error loading low stock products report:", error);
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => {
      const updatedFilters = {
        ...prev,
        [field]: value,
        ...(field === "category_id" && { sub_category_id: "", product_id: "" }),
        ...(field === "sub_category_id" && { product_id: "" }),
      };
      // Update ref immediately
      filtersRef.current = updatedFilters;
      return updatedFilters;
    });
  };

  const handleThresholdChange = (value) => {
    const numValue = parseInt(value) || 0;
    setThreshold(numValue);
    setFilters((prev) => {
      const updatedFilters = {
        ...prev,
        threshold: numValue,
      };
      // Update ref immediately
      filtersRef.current = updatedFilters;
      return updatedFilters;
    });
  };

  const handleSearch = () => {
    // Get the latest filters from ref (always up to date)
    const currentFilters = filtersRef.current;
    // Load report data with current filters
    loadReportData(currentFilters);
  };

  const handleReset = () => {
    const resetFilters = {
      category_id: "",
      sub_category_id: "",
      product_id: "",
      threshold: 10,
    };
    setFilters(resetFilters);
    setThreshold(10);
    // Update ref immediately
    filtersRef.current = resetFilters;
    // Load data with reset filters
    loadReportData(resetFilters);
  };

  const handleDownloadPDF = async () => {
    try {
      setDownloading(true);
      // Clean filters - remove empty strings and only send valid filter values
      const cleanedFilters = {};
      if (filters.category_id && filters.category_id !== "") {
        cleanedFilters.category_id = filters.category_id;
      }
      if (filters.sub_category_id && filters.sub_category_id !== "") {
        cleanedFilters.sub_category_id = filters.sub_category_id;
      }
      if (filters.product_id && filters.product_id !== "") {
        cleanedFilters.product_id = filters.product_id;
      }
      cleanedFilters.threshold = threshold || 10;

      const blob = await reportService.downloadReportPDF(
        "low-stock",
        cleanedFilters
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `low-stock-report-${
        new Date().toISOString().split("T")[0]
      }.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error downloading PDF:", error);
      alert("Failed to download PDF. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  // Filter products by category and subcategory
  const filteredProducts = products.filter((p) => {
    // Handle category filter - convert both to strings for comparison
    if (filters.category_id) {
      const productCategoryId =
        p.category_id != null ? String(p.category_id) : null;
      const filterCategoryId = String(filters.category_id);
      if (productCategoryId !== filterCategoryId) {
        return false;
      }
    }

    // Handle subcategory filter - only apply if subcategory is selected
    // Products with null sub_category_id should still show if only category is selected
    if (filters.sub_category_id) {
      const productSubCategoryId =
        p.sub_category_id != null ? String(p.sub_category_id) : null;
      const filterSubCategoryId = String(filters.sub_category_id);
      if (productSubCategoryId !== filterSubCategoryId) {
        return false;
      }
    }

    return true;
  });

  // Table headers - memoized to prevent re-renders
  const headers = useMemo(() => [
    {
      prop: "sr_no",
      title: "Sr. No.",
      isSortable: true,
      cell: (row) => row.sr_no || "-",
    },
    {
      prop: "name",
      title: "Product Name",
      isSortable: true,
      isFilterable: true,
      cell: (row) => row.name || row.product_name || "-",
    },
    {
      prop: "category_name",
      title: "Category",
      isSortable: true,
      isFilterable: true,
      cell: (row) => row.category_name || row.category?.name || "-",
    },
    {
      prop: "sub_category_name",
      title: "Sub Category",
      isSortable: true,
      cell: (row) => row.sub_category_name || row.sub_category?.name || "-",
    },
    {
      prop: "stock_quantity",
      title: "Current Stock",
      isSortable: true,
      cell: (row) => (
        <Badge
          bg={
            (row.stock_quantity || 0) <= threshold * 0.5
              ? "danger"
              : (row.stock_quantity || 0) <= threshold
              ? "warning"
              : "success"
          }
        >
          {row.stock_quantity || 0}
        </Badge>
      ),
    },
    {
      prop: "threshold",
      title: "Threshold",
      cell: () => threshold,
    },
    {
      prop: "price",
      title: "Price",
      isSortable: true,
      cell: (row) => `₹${parseFloat(row.price || 0).toFixed(2)}`,
    },
    {
      prop: "unit",
      title: "Unit",
      cell: (row) => row.unit || "-",
    },
    {
      prop: "is_active",
      title: "Status",
      isSortable: true,
      cell: (row) => (
        <Badge bg={row.is_active ? "success" : "secondary"}>
          {row.is_active ? "Active" : "Inactive"}
        </Badge>
      ),
    },
  ], [threshold]);

  const labels = useMemo(
    () => ({
      beforeSelect: " ",
    }),
    []
  );

  return (
    <Container fluid className="report-container">
      {/* <div className="report-header">
        <h2 className="report-title">Low Stock Products Report</h2>
        <div className="breadcrumb">Home &gt; Reports &gt; Low Stock Products</div>
      </div> */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <Link className="breadcrum" to="/">
          Home{"/"}
          <i
            className="fa-solid fa-chevron-right"
            style={{ marginLeft: "1px", marginRight: "1px" }}
          ></i>{" "}
          Reports / Low Stock Products
        </Link>
      </div>

      {/* {reportData.length > 0 && (
        <Alert variant="warning" className="mb-3">
          <FaExclamationTriangle /> <strong>Alert:</strong> {reportData.length}{" "}
          product(s) have stock below the threshold of {threshold} units.
        </Alert>
      )} */}

      <Card className="filter-card">
        <Card.Header className="filter-header">
          <h5>Filters</h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Category</Form.Label>
                <Form.Select
                  value={filters.category_id}
                  onChange={(e) =>
                    handleFilterChange("category_id", e.target.value)
                  }
                >
                  <option value="">All</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Sub Category</Form.Label>
                <Form.Select
                  value={filters.sub_category_id}
                  onChange={(e) =>
                    handleFilterChange("sub_category_id", e.target.value)
                  }
                  disabled={!filters.category_id}
                >
                  <option value="">All</option>
                  {subCategories.map((sub) => (
                    <option key={sub.id} value={sub.id}>
                      {sub.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Product</Form.Label>
                <Form.Select
                  value={filters.product_id}
                  onChange={(e) =>
                    handleFilterChange("product_id", e.target.value)
                  }
                >
                  <option value="">All</option>
                  {filteredProducts.length > 0 ? (
                    filteredProducts.map((prod) => (
                      <option key={prod.id} value={prod.id}>
                        {prod.product_name || prod.name}
                      </option>
                    ))
                  ) : (
                    <option disabled>No products available</option>
                  )}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Stock Threshold</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={threshold}
                  onChange={(e) => handleThresholdChange(e.target.value)}
                  placeholder="Enter threshold"
                />
              </Form.Group>
            </Col>
            <Col md={3} className="d-flex align-items-end mt-3">
              <Button
                variant="primary"
                className="report-btn-search"
                onClick={handleSearch}
              >
                <FaSearch /> Search
              </Button>
              <Button
                variant="outline-secondary"
                className="ms-2"
                onClick={handleReset}
              >
                Reset
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="report-content-card">
        <Card.Body>
          {reportData.length === 0 && loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <DatatableWrapper
              body={reportData}
              headers={headers}
              paginationOptionsProps={{
                initialState: { rowsPerPage: 10, options: [10, 25, 50, 100] },
              }}
            >
              <div className="compact-controls d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                <div className="d-flex align-items-center">
                  <div
                    className="flex-grow-1 me-2"
                    style={{ maxWidth: "300px" }}
                  >
                    <Filter
                      placeholder="Search products..."
                      className="w-100"
                    />
                  </div>
                  <PaginationOptions labels={labels} />
                  <InfoPill left="Total Products" right={reportData.length} />
                </div>

                <div>
                  <Button
                    variant="danger"
                    className="report-btn-download"
                    onClick={handleDownloadPDF}
                    disabled={downloading || loading || reportData.length === 0}
                  >
                    {downloading ? (
                      <>
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        Downloading...
                      </>
                    ) : (
                      <>
                        <FaFilePdf /> Download PDF
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Table */}
              <div className="table-responsive shadow-sm" style={{ position: "relative" }}>
                {loading && (
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: "rgba(255, 255, 255, 0.8)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 10,
                      borderRadius: "0.375rem",
                    }}
                  >
                    <Spinner animation="border" variant="primary" />
                  </div>
                )}
                <table className="table table-striped table-bordered align-middle">
                  <TableHeader />
                  <TableBody />
                </table>
              </div>

              {/* Pagination */}
              <div className="compact-pagination d-flex justify-content-left mt-3">
                <Pagination classes={{
                  button: "bg-dark",
                }} />
              </div>
            </DatatableWrapper>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default LowStockProductsReport;
